import { Box, ListSubheader, MenuItem, Select, Typography } from '@mui/material';
import { useAnalyzeAudience } from 'client/modules/Analyze/AnalyzeAudienceContext';
import { INK } from 'client/modules/Analyze/analyzeTheme';
import AnalyzeAudienceControls from 'client/modules/Analyze/components/AnalyzeAudienceControls';
import BreakdownTable from 'client/modules/Analyze/components/BreakdownTable';
import { useGetBreakdownQuery } from 'client/modules/Surveys/surveyAnalyzeApiSlice';
import { startCase } from 'lodash';
import ActivityIndicator from 'client/modules/App/components/ActivityIndicator/ActivityIndicator';
import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useOutletContext, useSearchParams } from 'react-router-dom';

const messages = defineMessages({
  dimension: { id: 'analyze.breakdown.dimension', defaultMessage: 'Break down by' },
  builtin: { id: 'analyze.breakdown.builtin', defaultMessage: 'Built-in' },
  properties: { id: 'analyze.breakdown.properties', defaultMessage: 'Properties' },
  groups: { id: 'analyze.breakdown.groups', defaultMessage: 'Groups' },
  tags: { id: 'analyze.breakdown.tags', defaultMessage: 'Tags' },
  savedSearches: { id: 'analyze.savedSearches', defaultMessage: 'Saved searches' },
  loadError: { id: 'analyze.breakdown.loadError', defaultMessage: 'Failed to load breakdown' },
  noData: { id: 'analyze.breakdown.noData', defaultMessage: 'No data for this dimension' },
  noProperties: {
    id: 'analyze.breakdown.noProperties',
    defaultMessage: 'No person properties available',
  },
});

const BUILTIN_DIMENSIONS = [
  { value: 'group', labelKey: 'groups' },
  { value: 'tags', labelKey: 'tags' },
  { value: 'saved_searches', labelKey: 'savedSearches' },
];

// Compact in-header dropdown matching the design's "Break down by" control.
const dimensionSelectSx = {
  bgcolor: '#fff',
  borderRadius: '8px',
  width: 'fit-content',
  '& .MuiSelect-select': {
    py: '7px',
    pl: '12px',
    pr: '34px !important',
    fontSize: 13,
    fontWeight: 600,
    color: INK,
    minHeight: 'unset',
    minWidth: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E7EE' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#C7CFDA' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#C7CFDA', borderWidth: '1px' },
  '& .MuiSelect-icon': { color: '#A6AFBD', right: 8 },
};

function AnalyzeBreakdownPage() {
  const { survey: outletSurvey } = useOutletContext();
  const { survey, audienceParams } = useAnalyzeAudience();
  const activeSurvey = survey || outletSurvey;
  const { formatMessage } = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const dimension = searchParams.get('breakdownBy') || 'group';

  const setDimension = useCallback(
    value => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (!value || value === 'group') {
            next.delete('breakdownBy');
          } else {
            next.set('breakdownBy', value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const propertyOptions = useMemo(() => {
    const types = activeSurvey?.person_properties_types || {};
    return Object.entries(types)
      .filter(([, { hidden }]) => !hidden)
      .map(([key]) => ({ key, label: startCase(key) }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  }, [activeSurvey?.person_properties_types]);

  const effectiveDimension = dimension.startsWith('property:') ? 'property' : dimension;
  const effectivePropertyKey = dimension.startsWith('property:')
    ? dimension.slice('property:'.length)
    : undefined;

  const query = useMemo(
    () => ({
      survey: activeSurvey?.id,
      ...audienceParams,
      dimension: effectiveDimension,
      property_key: effectiveDimension === 'property' ? effectivePropertyKey : undefined,
    }),
    [activeSurvey?.id, audienceParams, effectiveDimension, effectivePropertyKey],
  );

  const skip = !activeSurvey?.id || (effectiveDimension === 'property' && !effectivePropertyKey);

  const { data, isFetching, isError } = useGetBreakdownQuery(query, { skip });

  const handleDimensionChange = event => {
    setDimension(event.target.value);
  };

  if (!activeSurvey) {
    return null;
  }

  const rows = data?.rows || [];

  const dimensionControl = (
    <Select
      value={dimension}
      onChange={handleDimensionChange}
      sx={dimensionSelectSx}
      displayEmpty
      size="small"
      inputProps={{ 'aria-label': formatMessage(messages.dimension) }}
    >
      <ListSubheader>{formatMessage(messages.builtin)}</ListSubheader>
      {BUILTIN_DIMENSIONS.map(({ value, labelKey }) => (
        <MenuItem key={value} value={value}>
          {formatMessage(messages[labelKey])}
        </MenuItem>
      ))}
      {propertyOptions.length > 0 && (
        <ListSubheader>{formatMessage(messages.properties)}</ListSubheader>
      )}
      {propertyOptions.map(({ key, label }) => (
        <MenuItem key={key} value={`property:${key}`}>
          {label}
        </MenuItem>
      ))}
    </Select>
  );

  let placeholder = null;
  if (isFetching && !data) {
    placeholder = <ActivityIndicator />;
  } else if (isError && !data) {
    placeholder = <Typography color="error">{formatMessage(messages.loadError)}</Typography>;
  } else if (propertyOptions.length === 0 && effectiveDimension === 'property') {
    placeholder = <Typography>{formatMessage(messages.noProperties)}</Typography>;
  } else if (rows.length === 0) {
    placeholder = <Typography>{formatMessage(messages.noData)}</Typography>;
  }

  return (
    <Box>
      <AnalyzeAudienceControls />
      <BreakdownTable
        rows={rows}
        surveyId={activeSurvey.id}
        surveyType={data?.surveyType || activeSurvey.surveyType || 'nps'}
        dimension={effectiveDimension}
        propertyKey={effectivePropertyKey}
        dimensionControl={dimensionControl}
        placeholder={placeholder}
      />
    </Box>
  );
}

export default AnalyzeBreakdownPage;
